// src/components/events/EventsTab.js
// Events list view with cards, create button, and empty state.
// Routed directly at /dashboard/services (the dashboard's Events page).

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { subscribeToEvents, createEvent, updateEvent, deleteDraftEvent } from '../../services/event-service';
import EventCard, { getEventSortDate } from './EventCard';
import EventCreationForm from './EventCreationForm';
import { Plus, Calendar } from 'lucide-react';

const EventsTab = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [uid, setUid] = useState(null);

  // ── Auth + realtime subscription ──
  useEffect(() => {
    let unsubEvents = () => {};
    let done = false;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user && !done) {
        done = true;
        setUid(user.uid);
        unsubEvents = subscribeToEvents(user.uid, (evts) => {
          setEvents(evts);
          setLoading(false);
        });
      }
    });

    // Safety timeout
    const timeout = setTimeout(() => {
      if (!done) setLoading(false);
    }, 3000);

    return () => {
      unsubAuth();
      unsubEvents();
      clearTimeout(timeout);
    };
  }, []);

  // ── Sort: upcoming first, then past ──
  const today = new Date().toISOString().split('T')[0];
  const upcoming = events
    .filter(e => getEventSortDate(e) >= today && e.status !== 'cancelled' && e.status !== 'completed')
    .sort((a, b) => getEventSortDate(a).localeCompare(getEventSortDate(b)));
  const past = events
    .filter(e => getEventSortDate(e) < today || e.status === 'cancelled' || e.status === 'completed')
    .sort((a, b) => getEventSortDate(b).localeCompare(getEventSortDate(a)));

  // ── Handlers ──
  const handleView = useCallback((event) => {
    navigate(`/dashboard/events/${event.id}`);
  }, [navigate]);

  const handleEdit = useCallback((event) => {
    setEditingEvent(event);
    setShowCreateForm(true);
  }, []);

  const handleDuplicate = useCallback(async (event) => {
    try {
      const duplicateData = {
        title: `${event.title} (Copy)`,
        description: event.description,
        eventType: event.eventType,
        coverImage: event.coverImage,
        modalities: event.modalities || [],
        schedule: { ...event.schedule, sessionDates: [] },
        timezone: event.timezone,
        format: event.format,
        venue: event.venue,
        manualMeetingLink: event.manualMeetingLink,
        maxAttendees: event.maxAttendees,
        pricingModel: event.pricingModel,
        pricing: event.pricing || {},
        registrationDeadline: null,
        intakeFormId: event.intakeFormId,
        reminders: event.reminders,
        thankYouMessage: event.thankYouMessage,
        publishImmediately: false,
      };
      await createEvent(duplicateData);
    } catch (err) {
      console.error('Duplicate event error:', err);
      alert('Failed to duplicate event: ' + (err.message || 'Unknown error'));
    }
  }, []);

  const handleCancel = useCallback(async (event) => {
    if (!window.confirm(`Cancel "${event.title}"? This cannot be undone.`)) return;
    try {
      await updateEvent(event.id, { status: 'cancelled', cancelledAt: new Date().toISOString() });
    } catch (err) {
      console.error('Cancel event error:', err);
      alert('Failed to cancel event: ' + (err.message || 'Unknown error'));
    }
  }, []);

  const handleDelete = useCallback(async (event) => {
    if (!window.confirm(`Delete draft "${event.title}"? This cannot be undone.`)) return;
    try {
      await deleteDraftEvent(uid, event.id);
    } catch (err) {
      console.error('Delete event error:', err);
      alert('Failed to delete event: ' + (err.message || 'Unknown error'));
    }
  }, [uid]);

  const handleFormClose = useCallback(() => {
    setShowCreateForm(false);
    setEditingEvent(null);
  }, []);

  const handleFormSaved = useCallback(() => {
    setShowCreateForm(false);
    setEditingEvent(null);
    // Events list auto-updates via realtime subscription
  }, []);

  // ── Show creation/edit form as full-screen overlay ──
  if (showCreateForm) {
    return (
      <EventCreationForm
        existingEvent={editingEvent}
        onClose={handleFormClose}
        onSaved={handleFormSaved}
      />
    );
  }

  // ── Loading state ──
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #E5E7EB', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
            <p style={{ color: '#6B7280', fontSize: 14, fontFamily: 'Montserrat, sans-serif' }}>Loading events...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      </div>
    );
  }

  // ── Empty state ──
  if (events.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div style={{
            textAlign: 'center', padding: '60px 20px',
            background: '#FFFFFF', borderRadius: 12, border: '1px solid #E5E7EB',
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: '#F5F3FF',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Calendar size={28} color="#7c3aed" />
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 8px', fontFamily: 'Montserrat, sans-serif' }}>
              No events yet
            </h3>
            <p style={{ fontSize: 14, color: '#6B7280', margin: '0 0 24px', fontFamily: 'Montserrat, sans-serif', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
              Create your first event — workshops, classes, retreats, and more.
            </p>
            <button
              type="button"
              onClick={() => setShowCreateForm(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 24px', borderRadius: 8, border: 'none',
                background: '#7c3aed', color: '#FFFFFF', fontWeight: 600,
                fontSize: 14, fontFamily: 'Montserrat, sans-serif',
                cursor: 'pointer',
              }}
            >
              <Plus size={16} />
              Create event
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Events list ──
  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4">
      {/* Header with create button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0, fontFamily: 'Montserrat, sans-serif' }}>
          Your Events
        </h2>
        <button
          type="button"
          onClick={() => setShowCreateForm(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 18px', borderRadius: 8, border: 'none',
            background: '#7c3aed', color: '#FFFFFF', fontWeight: 600,
            fontSize: 14, fontFamily: 'Montserrat, sans-serif',
            cursor: 'pointer',
          }}
        >
          <Plus size={16} />
          Create event
        </button>
      </div>

      {/* Upcoming events */}
      {upcoming.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340, 1fr))',
            gap: 16,
          }}>
            {/* Use CSS media query approach via style */}
            <style>{`
              .events-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
              @media (max-width: 768px) { .events-grid { grid-template-columns: 1fr; } }
            `}</style>
          </div>
          <div className="events-grid">
            {upcoming.map(event => (
              <EventCard
                key={event.id}
                event={event}
                onView={handleView}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onCancel={handleCancel}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      )}

      {/* Past / completed / cancelled events */}
      {past.length > 0 && (
        <div>
          {upcoming.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
            }}>
              <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
              <span style={{ fontSize: 13, color: '#9CA3AF', fontWeight: 500, fontFamily: 'Montserrat, sans-serif' }}>
                Past & Completed
              </span>
              <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
            </div>
          )}
          <div className="events-grid">
            {past.map(event => (
              <EventCard
                key={event.id}
                event={event}
                onView={handleView}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onCancel={handleCancel}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default EventsTab;
